// Hash based client router, small on purpose instead of react-router.
//
// A hash route needs no server rewrite, which matters because the same
// bundle is loaded from a Tauri WebView over a custom protocol as well as
// from apps/web/server.ts over http. Deep links still work in both.
import { useEffect, useState } from "react";

/**
 * Round 5 shell. Five tabs, and every other screen is a destination reached
 * from one of them in a single tap, so no feature is more than two taps
 * from the tab bar.
 *
 * `capture` is deliberately NOT a tab any more: it has two entry points,
 * the floating control on the map (its natural home, capture is location
 * bound) and the Beranda shortcut (the write later path). The route itself
 * is unchanged, so old deep links still resolve.
 */
export const TAB_ROUTES = ["beranda", "map", "timeline", "cari", "profil"] as const;

export const ROUTES = [
  ...TAB_ROUTES,
  "capture",
  "search",
  "trips",
  "diary",
  "itinerary",
  "budget",
  "stats",
  "wishlist",
] as const;

export type RouteName = (typeof ROUTES)[number];
export type TabName = (typeof TAB_ROUTES)[number];

export const DEFAULT_ROUTE: RouteName = "beranda";

/**
 * Which tab stays lit while a destination screen is open, and where its back
 * control goes. Declared rather than inferred: a tab bar with nothing active
 * reads as broken, and a back button that guesses is worse than one that is
 * predictable.
 */
export const PARENT_OF: Record<RouteName, TabName> = {
  beranda: "beranda",
  map: "map",
  timeline: "timeline",
  cari: "cari",
  profil: "profil",
  capture: "map",
  search: "timeline",
  trips: "beranda",
  diary: "beranda",
  itinerary: "beranda",
  budget: "beranda",
  stats: "beranda",
  wishlist: "beranda",
};

export interface Route {
  name: RouteName;
  param: string | null;
}

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, "");
  const [head = "", param = ""] = raw.split("/");
  const name = (ROUTES as readonly string[]).includes(head) ? (head as RouteName) : DEFAULT_ROUTE;
  return { name, param: param ? decodeURIComponent(param) : null };
}

export function hrefFor(name: RouteName, param?: string): string {
  return param ? `#/${name}/${encodeURIComponent(param)}` : `#/${name}`;
}

export function navigate(name: RouteName, param?: string): void {
  const target = hrefFor(name, param);
  if (globalThis.location && globalThis.location.hash !== target) {
    globalThis.location.hash = target;
  }
}

/**
 * Has this session moved between screens yet. A traveller who deep links
 * straight into a destination has nothing behind them, so back has to fall
 * back to the parent tab instead of stepping out of the app.
 */
let hasMoved = false;
globalThis.addEventListener?.("hashchange", () => {
  hasMoved = true;
});

export function goBack(fallback: RouteName): void {
  if (hasMoved && (globalThis.history?.length ?? 0) > 1) {
    globalThis.history.back();
    return;
  }
  navigate(fallback);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(globalThis.location?.hash ?? ""));

  useEffect(() => {
    const onChange = () => setRoute(parseHash(globalThis.location?.hash ?? ""));
    globalThis.addEventListener?.("hashchange", onChange);
    onChange();
    return () => globalThis.removeEventListener?.("hashchange", onChange);
  }, []);

  return route;
}
